import type {
  PanderrNavigationItem,
  PanderrTemplateDefinition,
} from "../types";

export interface PanderrFooterSiteData {
  businessName?: string;
  businessDescription?: string;
  streetAddress?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  serviceArea?: string;
  phone?: string;
  email?: string;
  businessHours?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  navigation?: PanderrNavigationItem[];
}

function escapeHtml(value: string): string {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

const DEFAULT_DESCRIPTION =
  "Professional expertise, thoughtful service, and work designed to move your business forward.";

const DEFAULT_COLUMNS = [
  {
    heading: "Company",
    links: [
      { label: "About", href: "/about/" },
      { label: "Services", href: "/services/" },
      { label: "Our Work", href: "/our-work/" },
    ],
  },
  {
    heading: "Contact",
    links: [
      { label: "Contact Us", href: "/contact/" },
      { label: "(555) 555-5555", href: "tel:+15555555555" },
      { label: "hello@example.com", href: "mailto:hello@example.com" },
    ],
  },
];

function renderFooterLink(link: { label: string; href: string }): string {
  const href = String(link.href || "");
  const field = href.startsWith("tel:")
    ? ' data-panderr-field="site.contact.phone"'
    : href.startsWith("mailto:")
      ? ' data-panderr-field="site.contact.email"'
      : "";

  return `
    <a href="${escapeHtml(href)}"${field}>
      ${escapeHtml(link.label)}
    </a>
  `;
}

export function renderPanderrSocialLinks(
  siteData: PanderrFooterSiteData,
  includeEmpty = false,
): string {
  const links = [
    {
      key: "facebook",
      label: "Facebook",
      href: siteData.facebookUrl,
      icon: `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M13.5 22v-9h3l.5-3h-3.5V8.1c0-.9.3-1.6 1.7-1.6H17V3.8c-.8-.1-1.6-.2-2.4-.2-2.4 0-4.1 1.5-4.1 4.2V10H8v3h2.5v9h3z" />
      </svg>`,
    },
    {
      key: "instagram",
      label: "Instagram",
      href: siteData.instagramUrl,
      icon: `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm5 3.8A4.2 4.2 0 1 1 7.8 12 4.2 4.2 0 0 1 12 7.8zm0 2A2.2 2.2 0 1 0 14.2 12 2.2 2.2 0 0 0 12 9.8zm5.5-3.3a1 1 0 1 1-1 1 1 1 0 0 1 1-1z" />
      </svg>`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      href: siteData.linkedinUrl,
      icon: `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M5.3 7.3A2.3 2.3 0 1 1 5.3 2.7a2.3 2.3 0 0 1 0 4.6zM3.3 9h4v12h-4V9zm6.4 0h3.8v1.6h.1c.5-1 1.8-2.1 3.8-2.1 4 0 4.8 2.6 4.8 6.1V21h-4v-5.7c0-1.4 0-3.1-1.9-3.1s-2.2 1.5-2.2 3V21h-4V9z" />
      </svg>`,
    },
    {
      key: "youtube",
      label: "YouTube",
      href: siteData.youtubeUrl,
      icon: `<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M23 7.2a3 3 0 0 0-2.1-2.1C19 4.6 12 4.6 12 4.6s-7 0-8.9.5A3 3 0 0 0 1 7.2 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.8a3 3 0 0 0 2.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 0 0 2.1-2.1 31 31 0 0 0 .5-4.8 31 31 0 0 0-.5-4.8zM9.6 15.2V8.8L15.2 12l-5.6 3.2z" />
      </svg>`,
    },
  ].filter((link) => includeEmpty || Boolean(link.href?.trim()));

  if (!links.length) {
    return "";
  }

  return `
    <div
      class="panderr-footer-social"
      data-panderr-component="footer-social"
      aria-label="Social media"
    >
      ${links
        .map(
          (link) => `
            <a
              href="${escapeHtml(link.href?.trim() || "#")}"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="${link.label}"
              title="${link.label}"
              data-panderr-field="site.social.${link.key}"
              data-panderr-social-link="${link.key}"
            >
              ${link.icon}
            </a>
          `,
        )
        .join("")}
    </div>
  `;
}

export function renderPanderrFooter(
  template: PanderrTemplateDefinition | undefined,
  siteData: PanderrFooterSiteData = {},
): string {
  const businessName = siteData.businessName || "Your Business";
  const description =
    siteData.businessDescription ||
    template?.footer?.description ||
    DEFAULT_DESCRIPTION;

  const address = [
    siteData.streetAddress,
    siteData.addressLine2,
    siteData.city,
    siteData.state,
    siteData.zip,
  ]
    .filter(Boolean)
    .join(", ");

  const year = new Date().getFullYear();

  return `
    <footer
      class="panderr-footer panderr-site-footer"
      data-panderr-component="footer"
      data-panderr-global-component="footer"
    >
      <div class="panderr-shell-wide">
        <div class="panderr-footer-grid">
          <div class="panderr-footer-brand">
            <strong data-panderr-field="site.identity.businessName">
              ${escapeHtml(businessName)}
            </strong>

            <p
              data-panderr-field="site.identity.description"
              data-panderr-optional-field="true"
            >
              ${escapeHtml(description)}
            </p>

            <p>
              <a href="/privacy-policy/">Privacy Policy</a>
            </p>

            ${renderPanderrSocialLinks(siteData)}
          </div>

          <div class="panderr-footer-contact">
            <h3>Contact Details</h3>

            <div
              data-panderr-field="site.contact.address"
              data-panderr-optional-field="true"
            >
              ${escapeHtml(address)}
            </div>

            <div
              data-panderr-field="site.contact.serviceArea"
              data-panderr-optional-field="true"
            >
              ${escapeHtml(siteData.serviceArea || "")}
            </div>

            <div>
              Phone:
              <a
                href="${
                  siteData.phone
                    ? `tel:${escapeHtml(siteData.phone.replace(/[^\d+]/g, ""))}`
                    : "#"
                }"
                data-panderr-field="site.contact.phone"
                data-panderr-optional-field="true"
              >
                ${escapeHtml(siteData.phone || "")}
              </a>
            </div>

            <div>
              Email:
              <a
                href="${
                  siteData.email ? `mailto:${escapeHtml(siteData.email)}` : "#"
                }"
                data-panderr-field="site.contact.email"
                data-panderr-optional-field="true"
              >
                ${escapeHtml(siteData.email || "")}
              </a>
            </div>

            <div
              data-panderr-field="site.contact.hours"
              data-panderr-optional-field="true"
            >
              ${escapeHtml(siteData.businessHours || "")}
            </div>
          </div>
        </div>
        <div class="panderr-footer-bottom">
          <span data-panderr-copyright="true">
            Copyright ©${year}
            <span data-panderr-field="site.identity.businessName">
              ${escapeHtml(businessName)}
            </span>.
            All Rights Reserved.
          </span>

          <a
            href="https://panderr.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Created by Panderr.com
          </a>
        </div>
      </div>
    </footer>
  `;
}

export function createFooterCss(): string {
  return `
    .panderr-footer {
      padding: 3.5rem 0 1.75rem;
      background: var(--panderr-color-text);
      color: white;
    }

    .panderr-footer-grid {
      display: grid;
      grid-template-columns: minmax(280px, 1.35fr) minmax(240px, .8fr);
      gap: clamp(2.5rem, 5vw, 5rem);
    }

    .panderr-footer-brand {
      max-width: 430px;
    }

    .panderr-footer strong {
      display: block;
      margin-bottom: 1rem;
      font-size: 1.4rem;
    }

    .panderr-footer p,
    .panderr-footer a {
      color: rgba(255,255,255,.68);
    }

    .panderr-footer h3 {
      margin-bottom: 1rem;
      color: white;
      font-size: .82rem;
      text-transform: uppercase;
      letter-spacing: .12em;
    }

    .panderr-footer a {
      display: block;
      margin-bottom: .65rem;
    }

    .panderr-footer-social {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 1.25rem;
    }

    .panderr-footer-social a {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin: 0;
      color: rgba(255,255,255,.82);
    }

    .panderr-footer-bottom {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      margin-top: 2.5rem;
      padding-top: 1.5rem;
      border-top: 1px solid rgba(255,255,255,.12);
      font-size: .82rem;
      color: rgba(255,255,255,.48);
    }

    @media (max-width: 980px) {
      .panderr-footer-grid {
        grid-template-columns: repeat(2, 1fr);
      }

      .panderr-footer-brand {
        grid-column: 1 / -1;
      }
    }

    @media (max-width: 760px) {
      .panderr-footer-grid {
        grid-template-columns: 1fr;
      }

      .panderr-footer-brand {
        grid-column: auto;
      }

      .panderr-footer-bottom {
        flex-direction: column;
      }
    }
  `;
}
